import React from 'react';
import {styled} from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  CircularProgress,
  ListItem,
  ListItemButton,
  Tooltip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {useSnackbar} from 'notistack';

import {getPadsData} from '@shared/services/service-handlers/PrivateService';

import LogoComponent from '@modules/public/main/logo/LogoComponent';
import {
  closedMixin,
  DrawerHeader,
  openedMixin
} from '../../../common/top-app-bar/TopAppBarStyles';
import SmallLogoComponent from '@modules/public/main/small-logo/SmallLogoComponent';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {ListStyle} from './PadsMenuListStyle';
import IconComponent from '@components/icon/IconComponent';
import Theme from 'src/styles/theme';
import {APP} from 'src/styles/variables';
import {
  BASE_APPLICATION,
  BASE_ROUTE,
  BASE_ROUTE_PAD
} from 'src/routes/BaseRoute';
interface PadsMenuListComponentProps {
  drawerOpen: boolean;
  padId?: any;
  applicationId: any;
}

const PadsMenuList = (
  props: PadsMenuListComponentProps
): React.ReactElement => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const drawerWidth = 240;
  const {drawerOpen = true, applicationId, padId} = props;

  const [state, setState] = React.useState({
    activePad: undefined,
    padsList: [],
    loading: false
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'open'
  })(({theme, open}) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme)
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme)
    })
  }));

  const onChange = (item: any, index: any) => {
    setState((prevState: any) => ({
      ...prevState,
      activePad: index
    }));

    navigate(
      `/${BASE_ROUTE}/${BASE_APPLICATION}/${applicationId}/${BASE_ROUTE_PAD}/${item?.id}`
    );
  };

  const init = async () => {
    try {
      setState((prevState: any) => ({
        ...prevState,
        padsList: [],
        loading: true
      }));

      const res = await getPadsData(applicationId);

      setState((prevState: any) => ({
        ...prevState,
        padsList: res,
        loading: false
      }));

      if (padId) {
        const selectedPad = [...res].find((item: any) => item.id == padId);

        setState((prevState: any) => ({
          ...prevState,
          activePad: selectedPad?.id
        }));
      } else if (res?.length > 0 && res?.[0]?.id) {
        onChange(res[0], res[0].id);
      }
    } catch (error) {
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, padId]);

  const renderListItemButton = (item: any, index: number) => {
    return (
      <ListItemButton
        sx={{
          minHeight: 48,
          justifyContent: 'initial'
        }}
        selected={index === state.activePad}
        onClick={() => {
          onChange(item, index);
        }}
        style={{
          backgroundColor:
            index === state.activePad ? Theme.palette.common.white : 'none',
          borderLeft: '5px solid',
          borderColor:
            index === state.activePad
              ? `${APP.PALETTE.MODULE_LAYOUT_COLOR.MAIN}`
              : `${Theme.palette.common.white}`
        }}
        autoFocus={index === state.activePad ? true : false}>
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: 3,
            justifyContent: 'center'
          }}>
          {item?.icon ? (
            <IconComponent
              iconName={item?.icon}
              sx={{
                color:
                  index === state.activePad
                    ? `${APP.PALETTE.MODULE_LAYOUT_COLOR.MAIN}`
                    : ''
              }}
            />
          ) : null}
        </ListItemIcon>

        {item?.name ? (
          <ListItemText
            primaryTypographyProps={{
              fontWeight: index === state.activePad ? 'bold' : 'normal'
            }}
            primary={item?.name}
            sx={{
              opacity: 1,
              fontSize: 15,
              color:
                index === state.activePad
                  ? `${APP.PALETTE.MODULE_LAYOUT_COLOR.MAIN}`
                  : ''
            }}
          />
        ) : null}
      </ListItemButton>
    );
  };

  const listItems = (item: any, index: number) => {
    return (
      <Tooltip
        title={drawerOpen ? '' : item?.name}
        placement="right-start"
        enterDelay={100}
        leaveDelay={50}>
        {renderListItemButton(item, index)}
      </Tooltip>
    );
  };

  const renderItem = (item: any, index: number): any => {
    return (
      <ListItem key={index} disablePadding sx={{display: 'block'}}>
        {listItems(item, index)}
      </ListItem>
    );
  };

  const navbarListItems = () => {
    return (
      <ListStyle>
        {state?.loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%'
            }}>
            <CircularProgress />
          </div>
        ) : (
          state?.padsList?.map((item: any) => renderItem(item, item?.id))
        )}
      </ListStyle>
    );
  };

  return (
    <Box sx={{display: 'flex'}}>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <DrawerHeader
          style={{
            padding: '0',
            margin: drawerOpen ? '1px auto 3px 1.5em' : '0 auto'
          }}>
          {drawerOpen ? <LogoComponent /> : <SmallLogoComponent />}
        </DrawerHeader>
        {navbarListItems()}
      </Drawer>
    </Box>
  );
};
export default PadsMenuList;
